import {
  useState, useCallback, useRef 
} from 'react';
import { streamGenAI } from '@/lib/genai-client';
import type {
  AnalysisMode,
  InvoiceAnalysisResult,
  BoxCountResult,
  ProductVerificationResult,
} from '../types/ai-analysis';
import type { Delivery } from '../types';

type AnalysisResult = InvoiceAnalysisResult | BoxCountResult | ProductVerificationResult;

interface UseReceivingAIOptions {
  delivery?: Delivery | null;
  onAnalysisComplete?: (result: AnalysisResult, mode: AnalysisMode) => void;
}

interface UseReceivingAIReturn {
  isAnalyzing: boolean;
  streamingText: string;
  result: AnalysisResult | null;
  error: string | null;
  analyzeImage: (imageBase64: string, mediaType: 'image/jpeg' | 'image/png', mode: AnalysisMode) => Promise<void>;
  reset: () => void;
  cancel: () => void;
}

const PROMPT_KEYS: Record<AnalysisMode, string> = {
  invoice: 'invoice-analysis',
  'box-counting': 'box-counting',
  'product-verification': 'product-verification',
};

const USER_PROMPTS: Record<AnalysisMode, string> = {
  invoice: 'Please analyze this invoice/delivery document and extract all line items with their details.',
  'box-counting': 'Please count all boxes, pallets, and containers visible in this image and note any concerns.',
  'product-verification': 'Please read and extract all information from this product label including lot number, expiry date, and allergens.',
};

export function useReceivingAI(options: UseReceivingAIOptions = {}): UseReceivingAIReturn {
  const {
    delivery, onAnalysisComplete 
  } = options;

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setIsAnalyzing(false);
    setStreamingText('');
    setResult(null);
    setError(null);
  }, []);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsAnalyzing(false);
  }, []);

  const analyzeImage = useCallback(async (
    imageBase64: string,
    mediaType: 'image/jpeg' | 'image/png',
    mode: AnalysisMode
  ) => {
    // Reset state
    setIsAnalyzing(true);
    setStreamingText('');
    setResult(null);
    setError(null);

    // Create abort controller
    abortControllerRef.current = new AbortController();

    // Build context with delivery info if available
    const context: Record<string, unknown> = {};
    if (delivery) {
      context.poNumber = delivery.poNumber;
      context.supplierName = delivery.supplierName;
      context.expectedItems = delivery.lines.map((line) => ({
        itemName: line.itemName,
        sku: line.sku,
        orderedQuantity: line.orderedQuantity,
        unit: line.unit,
      }));
    }

    try {
      await streamGenAI<AnalysisResult>(
        {
          promptKey: PROMPT_KEYS[mode],
          userPrompt: USER_PROMPTS[mode],
          context,
          image: {
            base64: imageBase64,
            mediaType,
          },
        },
        // onChunk
        (chunk) => {
          setStreamingText((prev) => prev + chunk);
        },
        // onComplete
        (analysisResult) => {
          setResult(analysisResult);
          setIsAnalyzing(false);
          onAnalysisComplete?.(analysisResult, mode);
        },
        // onError
        (err) => {
          setError(err.message);
          setIsAnalyzing(false);
        },
        abortControllerRef.current.signal
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Analysis failed';
      setError(message);
      setIsAnalyzing(false);
    }
  }, [delivery, onAnalysisComplete]);

  return {
    isAnalyzing,
    streamingText,
    result,
    error,
    analyzeImage,
    reset,
    cancel,
  };
}
